<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { useBasePriceStore } from "@/modules/base_price/store";
import { budgetAllocationIssue } from "@/modules/base_price/utils/budgetAllocation";
import PlanBudgetSplitTab from "@/modules/base_price/components/PlanBudgetSplitTab.vue";
import PlanSeasonalityTab from "@/modules/base_price/components/PlanSeasonalityTab.vue";
import PlanFillCpmTab from "@/modules/base_price/components/PlanFillCpmTab.vue";
import PlanImpressionShareTab from "@/modules/base_price/components/PlanImpressionShareTab.vue";

const store = useBasePriceStore();

/** The node or screen every tab below edits — see PlanEditTarget. */
const target = computed(() => store.editTarget);

const isOpenBook = computed(() => store.selectedBook?.type === "open_inventory");

type TabDef = { id: string; label: string; error?: string };

/**
 * The two ways a target can promise away more than it has, mapped to the tab
 * that can fix each — the tab-level half of the hierarchy's red dot, so a
 * flagged node says *where* the problem is rather than leaving you to open
 * tabs until you find it.
 *
 * A screen only ever has the seasonality one: it has no budget split, since
 * it has no children to divide anything among. Its Budget split tab doesn't
 * exist, so there is nothing to mark.
 */
const budgetSplitError = computed(() => {
	if (store.selectedAsset || !store.selectedNode) return undefined;

	const issue = budgetAllocationIssue(store.selectedNode);
	if (issue === "over") return "This node's children ask for more than its budget";
	if (issue === "under") return "This node hasn't assigned its entire budget to its children";

	return undefined;
});

const seasonalityError = computed(() => {
	if (!store.editTarget) return undefined;
	if (!store.editTarget.season_fully_allocated) {
		return "The months pinned here don't fit this target's budget";
	}
	if (!store.editTarget.days_fully_allocated) {
		return "The days pinned here don't fit their month's budget";
	}

	return undefined;
});

const impressionShareError = computed(() => {
	const target = store.editTarget;
	if (!target) return undefined;

	const over =
		target.kind === "asset"
			? store.overReservedAssetIds.has(target.id)
			: store.nodeOverReserved(target.id);

	if (over) return "More than one book reserves this inventory beyond what exists";

	const shareIssue = store.shareBudgetIssue(target.kind, target.id);
	if (shareIssue === "budget_without_share") {
		return "This reserves nothing all year but still gets budget, which it can't earn";
	}
	if (shareIssue === "share_without_budget") {
		return "This reserves impressions but has no budget, so they go for €0";
	}

	return undefined;
});

const fillCpmError = computed(() =>
	store.editTarget && !store.editTarget.cpm_within_bounds
		? "A hand-set CPM here sits outside this target's price bounds"
		: undefined
);

/**
 * The tab set depends on what is selected. The two book types have different
 * levers, matching the original design; a screen has fewer still — it carries
 * its own seasonality, fill and manual CPM (see ResolvedAssetDTO) but no
 * budget split, since it has no children to divide anything among. Its own
 * budget is set one level up, on its node's Budget split tab, as a fixed euro
 * amount.
 */
const tabs = computed<TabDef[]>(() => {
	const split = {
		id: "split",
		label: isOpenBook.value ? "Budget split" : "Split & inventory",
		error: budgetSplitError.value,
	};
	const impressionShare = {
		id: "impression_share",
		label: "Impression share",
		error: impressionShareError.value,
	};
	const seasonality = {
		id: "seasonality",
		label: "Monthly Budget Distribution",
		error: seasonalityError.value,
	};
	const price = isOpenBook.value
		? { id: "fill_cpm", label: "CPM & Fill", error: fillCpmError.value }
		: { id: "cpm_per_month", label: "CPM per month", error: fillCpmError.value };

	// A screen gets the same levers as a node bar the budget split, which is
	// about dividing money among children it doesn't have. Impression share is
	// emphatically among them: a screen can reserve at its own rate, which is
	// the whole reason the resolver tracks inventory per screen.
	const forAsset = isOpenBook.value
		? [seasonality, price]
		: [impressionShare, seasonality, price];

	if (store.selectedAsset) {
		return forAsset;
	}

	return isOpenBook.value
		? [split, seasonality, price]
		: [split, impressionShare, seasonality, price];
});

/**
 * Keeps the open tab inside whatever tab set is current. Selecting a screen
 * drops the Budget split tab, so anyone sitting on it lands on Seasonality
 * rather than on a blank panel; the same guard covers switching between the
 * open-inventory and reservation tab sets.
 */
watch(
	tabs,
	(available) => {
		if (!available.some((tab) => tab.id === store.activeDetailTab)) {
			store.activeDetailTab = available[0].id;
		}
	},
	{ immediate: true }
);

// Tab ids only fully overlap on "split" across the two tab sets — reset to
// it whenever the book (and therefore the tab set) changes. Lives in the
// store (store.activeDetailTab), not a local ref, so the hierarchy
// sidebar's orange override marker can be contextual to whichever tab is
// open — see PlanHierarchyRow.vue.
watch(
	() => store.selectedBookUuid,
	() => (store.activeDetailTab = "split")
);

const scrollRef = ref();

/**
 * The panel is one scroll container shared by every node, screen and tab, so
 * without this a new selection opened at the previous one's scroll depth —
 * often below where its (shorter) content ends, showing nothing at all.
 */
watch(
	() => [
		store.selectedNodeId,
		store.selectedAssetId,
		store.selectedBookUuid,
		store.activeDetailTab,
	],
	() => {
		if (scrollRef.value?.$el) scrollRef.value.$el.scrollTop = 0;
	}
);

const breadcrumbLabel = computed(() => {
	if (!store.selectedNode) return "";

	const ancestors = store.ancestorsOf(store.selectedNode.hierarchy_node_id);
	const trail = [store.selectedBook?.name, ...ancestors.map((node) => node.name)];

	// A screen sits one level below its node, so that node joins the trail —
	// the heading below is the screen's name, not its.
	if (store.selectedAsset) trail.push(store.selectedNode.name);

	return trail.join(" · ");
});

const impressionsLabel = computed(() =>
	isOpenBook.value ? "Open impressions" : "Reserved impressions"
);
</script>

<template>
	<div v-if="target && store.selectedNode" class="d-flex flex-column h-100">
		<div class="px-6 pt-4">
			<div class="text-caption text-medium-emphasis mb-1">
				{{ breadcrumbLabel }}
			</div>
			<div class="d-flex align-baseline ga-3 flex-wrap">
				<div class="text-h6 font-weight-bold">
					{{ target.name }}
				</div>
				<div class="text-caption text-medium-emphasis">
					{{ target.kindLabel }}
					<!--
						A screen's key, the name the rest of the app knows it by —
						left out when the screen has no display name, since the
						heading already is the key then.
					-->
					<template v-if="target.nameClean && target.nameClean !== target.name">
						· {{ target.nameClean }}
					</template>
				</div>
			</div>

			<div class="d-flex flex-wrap ga-8 my-4">
				<div>
					<div class="text-caption text-medium-emphasis mb-1">Budget</div>
					<div class="text-body-1 font-weight-bold">
						{{ formatNumberForScale("money", target.budget) }}
					</div>
				</div>
				<div>
					<div class="text-caption text-medium-emphasis mb-1">
						{{ impressionsLabel }}
					</div>
					<div class="text-body-1 font-weight-bold">
						{{ formatNumberForScale("number_no_fraction", target.gross_impressions) }}
					</div>
				</div>
				<div>
					<div class="text-caption text-medium-emphasis mb-1">Sellable</div>
					<div class="text-body-1 font-weight-bold">
						{{ formatNumberForScale("number_no_fraction", target.sellable_impressions) }}
					</div>
				</div>
				<div>
					<div class="text-caption text-medium-emphasis mb-1">
						CPM year (derived)
					</div>
					<div class="text-body-1 font-weight-bold">
						{{ formatNumber(target.cpm, "money") }}
					</div>
				</div>
				<div>
					<div class="text-caption text-medium-emphasis mb-1">
						Expected revenue
					</div>
					<div class="text-body-1 font-weight-bold">
						{{ formatNumberForScale("money", target.revenue) }}
					</div>
				</div>
			</div>

			<v-tabs v-model="store.activeDetailTab" density="compact">
				<v-tab v-for="tab in tabs" :key="tab.id" :value="tab.id">
					<span class="d-flex align-center ga-2">
						<span>{{ tab.label }}</span>
						<span v-if="tab.error" class="tab-error-dot" :title="tab.error" />
					</span>
				</v-tab>
			</v-tabs>
		</div>
		<v-divider />

		<perfect-scrollbar
			ref="scrollRef"
			class="flex-grow-1"
			:options="{ suppressScrollX: true }"
		>
			<div class="pa-6">
				<plan-budget-split-tab
					v-if="store.activeDetailTab === 'split'"
					:node="store.selectedNode"
				/>
				<plan-seasonality-tab
					v-else-if="store.activeDetailTab === 'seasonality'"
					:target="target"
				/>
				<plan-fill-cpm-tab
					v-else-if="
						store.activeDetailTab === 'fill_cpm' ||
						store.activeDetailTab === 'cpm_per_month'
					"
					:target="target"
				/>
				<plan-impression-share-tab
					v-else-if="store.activeDetailTab === 'impression_share'"
					:target="target"
				/>
			</div>
		</perfect-scrollbar>
	</div>
</template>

<style scoped lang="scss">
// Same marker the hierarchy uses for an over-committed node — see
// PlanHierarchyRow.vue's allocation-error-dot — so the dot in the tree and
// the dot on the tab read as the same statement.
.tab-error-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: rgb(var(--v-theme-error));
	flex: 0 0 auto;
}
</style>
